import { prefersReducedMotion } from '@theme/utilities';

const DEFAULT_EASE = 0.02;
const DEFAULT_WANDER = 0.6;
const WANDER_SPEED = 0.00008;

class HeroGradientBlob extends HTMLElement {
  #surface;
  #raf = null;
  #io = null;
  #running = false;
  #intersecting = false;
  #ready = false;
  #startDelay = 0;
  #startTimer = null;
  #t0 = 0;
  #cur = { x: 0, y: 0 };
  #ptr = { x: 0, y: 0, active: false };
  #ease = DEFAULT_EASE;
  #wander = DEFAULT_WANDER;

  connectedCallback() {
    this.#surface = this.closest('.hero__container') ?? this.parentElement ?? this;
    this.#ease = Number(this.dataset.ease) || DEFAULT_EASE;
    const wanderAttr = Number(this.dataset.wander);
    this.#wander = Number.isFinite(wanderAttr) ? wanderAttr : DEFAULT_WANDER;
    const delayAttr = Number(this.dataset.startDelay);
    this.#startDelay = Number.isFinite(delayAttr) ? delayAttr : 0;

    if (prefersReducedMotion()) {
      this.classList.add('is-active');
      return;
    }

    requestAnimationFrame(() => this.classList.add('is-active'));

    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          this.#intersecting = entry.isIntersecting;
          if (!this.#ready) return;
          if (entry.isIntersecting) this.#start();
          else this.#stop();
        }),
      { threshold: 0 }
    );
    this.#io.observe(this.#surface);

    if (this.#startDelay > 0) {
      this.#startTimer = setTimeout(this.#activate, this.#startDelay);
    } else {
      this.#activate();
    }
  }

  disconnectedCallback() {
    this.#stop();
    clearTimeout(this.#startTimer);
    this.#io?.disconnect();
    this.#io = null;
    this.#surface?.removeEventListener('pointerenter', this.#onMove);
    this.#surface?.removeEventListener('pointermove', this.#onMove);
    this.#surface?.removeEventListener('pointerdown', this.#onMove);
    this.#surface?.removeEventListener('pointerleave', this.#onLeave);
    this.#surface?.removeEventListener('pointerup', this.#onLeave);
    this.#surface?.removeEventListener('pointercancel', this.#onLeave);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #activate = () => {
    if (!this.isConnected) return;
    this.#ready = true;
    const opts = { passive: true };
    this.#surface.addEventListener('pointerenter', this.#onMove, opts);
    this.#surface.addEventListener('pointermove', this.#onMove, opts);
    this.#surface.addEventListener('pointerdown', this.#onMove, opts);
    this.#surface.addEventListener('pointerleave', this.#onLeave, opts);
    this.#surface.addEventListener('pointerup', this.#onLeave, opts);
    this.#surface.addEventListener('pointercancel', this.#onLeave, opts);
    if (this.#intersecting) this.#start();
  };

  #onMove = (event) => {
    this.#ptr.x = event.clientX;
    this.#ptr.y = event.clientY;
    this.#ptr.active = true;
  };

  #onLeave = () => {
    this.#ptr.active = false;
  };

  #onVisibility = () => {
    if (document.hidden) this.#stop();
    else if (this.#ready && this.#intersecting) this.#start();
  };

  #start() {
    if (this.#running) return;
    this.#running = true;
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop() {
    this.#running = false;
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #tick = (now) => {
    if (!this.#running) return;
    if (!this.#t0) this.#t0 = now;

    const rect = this.#surface.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    let targetX;
    let targetY;
    if (this.#ptr.active) {
      targetX = this.#ptr.x - centerX;
      targetY = this.#ptr.y - centerY;
    } else {
      const elapsed = now - this.#t0;
      targetX = Math.sin(elapsed * WANDER_SPEED) * (rect.width / 2) * this.#wander;
      targetY = Math.sin(elapsed * WANDER_SPEED * 1.3) * (rect.height / 2) * this.#wander;
    }

    this.#cur.x += (targetX - this.#cur.x) * this.#ease;
    this.#cur.y += (targetY - this.#cur.y) * this.#ease;

    this.style.setProperty('--blob-dx', `${this.#cur.x.toFixed(2)}px`);
    this.style.setProperty('--blob-dy', `${this.#cur.y.toFixed(2)}px`);

    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('hero-gradient-blob')) {
  customElements.define('hero-gradient-blob', HeroGradientBlob);
}
